import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PaginatedFooter from 'src/components/paginated-footer/paginated-footer';
import FilterTags from 'src/components/tags/filter-tags';
import EmptyPlaceholder from './empty-placeholder';
import List from './list';
import css from './services.module.scss';
import { useGetInfiniteServiceBlocks, useGetTags } from './api/queries';
import { ServiceProps, Tag } from 'src/types';

export default function Services({ filters, className, pages }: Readonly<ServiceProps>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTag = { id: null, name: 'Усі' };
  const slug = searchParams?.get('slug');
  const selectedService = document?.getElementById(`${slug}`) ?? null;
  const selectedTag = searchParams.get('tag') ?? null;

  const { data: tags } = useGetTags(filters);
  const combinedTags = [defaultTag].concat(tags);
  const skeletons = [...Array(3).keys()].map((i) => <span key={i} className={css.skeleton} />);

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useGetInfiniteServiceBlocks({
    ...filters,
    tagId: tags?.find((t: Tag) => selectedTag && t.name?.includes(selectedTag))?.id ?? null
  });

  const servicesBlocks = data?.pages.flatMap((page) => page?.items) || [];
  const totalBlockCount = data?.pages[0]?.totalBlockCount ?? 0;
  const totalServicesCount = data?.pages[0]?.totalServicesCount ?? 0;

  const setSearchTag = useCallback(
    (data: string) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      const tag = tags?.find((t: Tag) => t.id?.includes(data) || t.name?.includes(data));
      if (selectedTag === tag?.name) {
        return;
      }
      if (tag) {
        nextSearchParams.set('tag', tag?.name);
      } else if (!selectedTag || !data) {
        nextSearchParams.delete('tag');
      }

      setSearchParams(nextSearchParams);
    },
    [searchParams, selectedTag, setSearchParams, tags]
  );

  const handleTagChange = useCallback((tagId: string) => setSearchTag(tagId), [setSearchTag]);

  const hidePaginatedFooter =
    isFetching || servicesBlocks?.length === 0 || totalBlockCount?.length === 0 || !hasNextPage;

  useEffect(() => {
    if (selectedTag) {
      handleTagChange(selectedTag);
    }
  }, [handleTagChange, selectedTag, setSearchTag]);

  useEffect(() => {
    if (selectedService) {
      selectedService?.scrollIntoView({ behavior: 'auto', block: 'center' });

      const nextSearchParams = new URLSearchParams(searchParams);
      slug && selectedService && nextSearchParams.delete('slug');
      if (nextSearchParams.toString() !== searchParams.toString()) setSearchParams(nextSearchParams);
    }
  }, [searchParams, selectedService, setSearchParams, slug]);

  return (
    <div className={css.services}>
      <div className={css.body}>
        {tags?.length > 0 && (
          <FilterTags
            title={''}
            tags={combinedTags.map((t) => t.id)}
            selectedTag={tags?.find((t: Tag) => selectedTag && t.name?.includes(selectedTag))?.id ?? null}
            calcName={(tagId) => combinedTags.find((t) => t.id === tagId)?.name}
            onClick={handleTagChange}
          />
        )}
        <List
          key={tags?.find((t: Tag) => selectedTag && t.name?.includes(selectedTag))?.id ?? null}
          servicesLimit={5}
          servicesBlocks={servicesBlocks}
          className={className}
          showCardsPagination
          tag={selectedTag}
          pages={pages}
        />
        {!isFetching && totalServicesCount === 0 && <EmptyPlaceholder />}
        {!hidePaginatedFooter && (
          <div className={css.pagerRow}>
            <PaginatedFooter caption='Більше послуг' disabled={isFetchingNextPage} onClick={fetchNextPage} />
          </div>
        )}
      </div>
      {isFetching && <div className={css.skeletons}>{skeletons}</div>}
    </div>
  );
}
